import {useState, useRef, useEffect} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {ROUTES} from '../../../../_gori/constants'
import {LocationState} from '../../../../types/locationTypes'
import {TutorialModal, TutorialPanel, TutorialType} from './TutorialModal'

const HASH_LENGTH = 15

export function GoriSignatureManual() {
  const [code, setCode] = useState(Array(HASH_LENGTH).fill(''))
  const [tutorialType, setTutorialType] = useState<TutorialType | null>(null)
  const [showPanel, setShowPanel] = useState(true)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()
  const location = useLocation() as {
    pathname: string
    state?: LocationState
  }
  const intl = useIntl()

  useEffect(() => {
    const errorStateMessage = location.state?.error
    if (errorStateMessage) {
      toast.error(errorStateMessage)
      navigate(location.pathname, {replace: true})
    }
  }, [location, navigate])

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) value = value[0]

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    if (value && index < HASH_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, HASH_LENGTH)
    const newCode = [...code]
    for (let i = 0; i < pastedData.length && index + i < HASH_LENGTH; i++) {
      newCode[index + i] = pastedData[i]
    }
    setCode(newCode)
    const nextIndex = Math.min(index + pastedData.length, HASH_LENGTH - 1)
    inputRefs.current[nextIndex]?.focus()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const hash = code.join('')
    if (hash.length === HASH_LENGTH) {
      navigate(ROUTES.GORI_SIGNATURE.METHOD_SELECT, {
        state: {hash: hash},
      })
    }
  }

  return (
    <div className='gori-signature-page'>
      <div className='gori-signature-header-wrapper text-center'>
        <img
          src='/media/logos/gori_signature_logo.png'
          alt={intl.formatMessage({id: 'GORI_SIGNATURE_TITLE'})}
          className='gori-signature-logo mb-5'
        />
        <p className='text-dark text-center'>
          {intl.formatMessage({id: 'GORI_SIGNATURE_MANUAL_INSTRUCTION_PREFIX'})}{' '}
          <span
            className='gori-signature-tutorial__inline-link'
            role='button'
            tabIndex={0}
            onClick={() => setTutorialType('ref4')}
            onKeyDown={(e) => e.key === 'Enter' && setTutorialType('ref4')}
          >
            {intl.formatMessage({id: 'GORI_SIGNATURE_TUTORIAL_REF4_TITLE'})}
          </span>{' '}
          {intl.formatMessage({id: 'GORI_SIGNATURE_MANUAL_INSTRUCTION_MID'})}{' '}
          <span
            className='gori-signature-tutorial__inline-link'
            role='button'
            tabIndex={0}
            onClick={() => setTutorialType('qr')}
            onKeyDown={(e) => e.key === 'Enter' && setTutorialType('qr')}
          >
            {intl.formatMessage({id: 'GORI_SIGNATURE_TRY_DIFFERENT_METHOD'})}
          </span>
          .
        </p>
      </div>

      <form onSubmit={handleSubmit} className='w-100 gori-signature-manual-form'>
        <div className='gori-signature-input-container rounded-3 d-flex justify-content-center'>
          <div className='gori-signature-input-inner d-flex align-items-center'>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type='text'
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(index, e)}
                className='gori-signature-input form-control form-control-lg text-center fw-semibold'
                aria-label={`Character ${index + 1} of ${HASH_LENGTH}`}
                aria-required='true'
              />
            ))}
          </div>
        </div>

        <div className='text-center'>
          <button
            type='submit'
            disabled={code.join('').length !== HASH_LENGTH}
            className='gori-signature-submit btn btn-outline btn-primary-deep hover-orange rounded-pill'
            aria-label={intl.formatMessage({id: 'GORI_SIGNATURE_SUBMIT'})}
          >
            {intl.formatMessage({id: 'GORI_SIGNATURE_SUBMIT'})}
          </button>
        </div>
      </form>

      <TutorialModal
        show={!!tutorialType}
        type={tutorialType || 'ref4'}
        handleClose={() => setTutorialType(null)}
      />

      <TutorialPanel show={showPanel} handleClose={() => setShowPanel(false)} />
    </div>
  )
}
